/**
 * 요금제 관리 비즈니스 로직
 * 정렬, 검증, 포맷팅 등의 로직
 */

import type { PricingPlan, PricingType } from '../models';

export class PricingService {
  /**
   * 요금제 정렬
   * 1. 활성화 상태 (활성화된 것이 먼저)
   * 2. 타입 순서 (hourly → daily → weekly → monthly)
   * 3. 같은 타입 내에서는 기간 순
   * 4. 같은 기간이면 가격 순
   */
  sortPricingPlans(plans: PricingPlan[]): PricingPlan[] {
    const typeOrder: Record<PricingType, number> = {
      hourly: 0,
      daily: 1,
      weekly: 2,
      monthly: 3,
    };

    return [...plans].sort((a, b) => {
      // 1. 활성화 상태 (활성화된 것이 먼저)
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }

      // 2. 타입 순서
      const typeA = typeOrder[a.type] ?? 999;
      const typeB = typeOrder[b.type] ?? 999;
      if (typeA !== typeB) {
        return typeA - typeB;
      }

      // 3. 같은 타입 내에서는 기간 순
      if (a.duration_hours !== b.duration_hours) {
        return a.duration_hours - b.duration_hours;
      }

      // 4. 같은 기간이면 가격 순
      return a.base_price - b.base_price;
    });
  }

  /**
   * 요금제 타입 레이블 가져오기
   */
  getPricingTypeLabel(type: PricingType): string {
    const labels: Record<PricingType, string> = {
      hourly: '시간권',
      daily: '일일권',
      weekly: '주간권',
      monthly: '월간권',
    };
    return labels[type] || type;
  }

  /**
   * 기간 포맷팅
   */
  formatDuration(hours: number, type: PricingType): string {
    if (type === 'hourly') {
      return `${hours}시간`;
    } else if (type === 'daily') {
      const days = Math.floor(hours / 24);
      return `${days}일`;
    } else if (type === 'weekly') {
      const weeks = Math.floor(hours / 168);
      return `${weeks}주`;
    } else if (type === 'monthly') {
      const months = Math.floor(hours / 720);
      return `${months}개월`;
    }
    return `${hours}시간`;
  }

  /**
   * 가격 포맷팅
   */
  formatPrice(price: number): string {
    return `₩${price.toLocaleString()}`;
  }

  /**
   * 요일 이름 가져오기
   */
  getDayName(dayOfWeek: number): string {
    const days = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    return days[dayOfWeek] || '';
  }

  /**
   * 시간 포맷팅 (HH:MM)
   */
  formatTime(time: string): string {
    if (!time) return '';
    return time.substring(0, 5); // "HH:MM:SS" → "HH:MM"
  }

  /**
   * 요금제 검증
   */
  validatePricingPlan(data: {
    name: string;
    duration_hours: number;
    base_price: number;
  }): { valid: boolean; message?: string } {
    if (!data.name || data.name.trim() === '') {
      return { valid: false, message: '요금제 이름을 입력해주세요.' };
    }

    if (data.duration_hours <= 0) {
      return { valid: false, message: '기간은 0보다 커야 합니다.' };
    }

    // 0원(무료 체험 상품)은 허용, 음수만 막는다 (2026-11-03)
    if (!(data.base_price >= 0)) {
      return { valid: false, message: '가격은 0원 이상으로 입력해주세요.' };
    }

    return { valid: true };
  }

  /**
   * 운영시간 검증
   */
  validateOperatingHours(data: {
    open_time: string;
    close_time: string;
  }): { valid: boolean; message?: string } {
    if (!data.open_time || !data.close_time) {
      return { valid: false, message: '운영시간을 입력해주세요.' };
    }

    // 시간 형식 검증 (HH:MM)
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(data.open_time) || !timeRegex.test(data.close_time)) {
      return { valid: false, message: '올바른 시간 형식(HH:MM)을 입력해주세요.' };
    }

    return { valid: true };
  }

  /**
   * 타입별 기본 기간 가져오기
   */
  getDefaultDuration(type: PricingType): number {
    const defaults: Record<PricingType, number> = {
      hourly: 1,
      daily: 24,
      weekly: 168,
      monthly: 720,
    };
    return defaults[type] || 1;
  }
}

// 싱글톤 인스턴스
export const pricingService = new PricingService();
